import type { ILenderProgram, IResponsePayment, PaymentTerm, TermsById } from '@makemydeal/dr-platform-types';

export type TermRow = Partial<Pick<PaymentTerm, 'months' | 'creditTier'>> &
    Pick<IResponsePayment, 'compatibleIncentiveIds'> & {
        lenderProgram?: Partial<ILenderProgram>;
        incentivesContext?: { programContextId?: string; compatibleIncentiveIds?: number[] };
        appliedIncentiveIds?: unknown;
    };

export type TermEligibility = { compatibleIds: Set<number>; appliedIncentiveIds: unknown };

const TERM_PROGRAM_IDENTITY_KEYS: ReadonlyArray<keyof ILenderProgram> = [
    'branchId',
    'lenderId',
    'programName',
    'programType',
    'regionalId'
];

const buildProgramIdentity = (term: TermRow): string =>
    [...TERM_PROGRAM_IDENTITY_KEYS.map((key) => term.lenderProgram?.[key] ?? ''), term.creditTier ?? ''].join('|');

/**
 * Same recipe as dash-store's `buildProgramContextId`, duplicated because offer-redux cannot depend
 * on dash-store.
 */
const buildTermIdentity = (term: TermRow): string => [term.months, buildProgramIdentity(term)].join('|');

/**
 * An empty array counts as no data, as in `getCompatibleIncentiveIdsForTerm`: payment-lambda cannot
 * tell "compatible with nothing" from "nothing resolved for this term".
 */
const readOwnCompatibleIncentiveIds = (term: TermRow): Set<number> | null => {
    const direct = term.compatibleIncentiveIds;
    if (Array.isArray(direct) && direct.length > 0) return new Set<number>(direct);

    const context = term.incentivesContext;
    if (!context?.programContextId || context.programContextId !== buildTermIdentity(term)) return null;

    const fromContext = context.compatibleIncentiveIds;
    return Array.isArray(fromContext) && fromContext.length > 0 ? new Set<number>(fromContext) : null;
};

export const getTermProgramIdentity = (term: TermRow | undefined): string | null => (term ? buildProgramIdentity(term) : null);

/**
 * Keeps the newest same-months row with usable data, not the first: the first row can be one this
 * request is still creating.
 */
export const findTermEligibilityForMonths = (
    termsById: TermsById,
    termIds: string[],
    months: number,
    programIdentity: string | null
): TermEligibility | null => {
    let newest: TermEligibility | null = null;
    for (const termId of termIds) {
        const term = termsById[termId] as TermRow | undefined;
        if (!term || term.months !== months) continue;
        if (programIdentity !== null && buildProgramIdentity(term) !== programIdentity) continue;
        const compatibleIds = readOwnCompatibleIncentiveIds(term);
        if (compatibleIds != null) newest = { compatibleIds, appliedIncentiveIds: term.appliedIncentiveIds };
    }
    return newest;
};
